import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { Button } from '@shared/components/ui/button';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { Search } from '@shared/components/ui/Search';
import { TotalResults } from '@shared/components/ui/TotalResults';
import type { CourierInvoiceListItem } from '../../../models/types';
import { useCourierInvoicesInfiniteList } from '../../hooks/useCourierInvoicesInfiniteList';
import { ErrorState, LoadingState } from '../feedback';
import { CourierInvoicesAgGridTable } from '../display/CourierInvoicesAgGridTable';
import { CreateCourierInvoiceModal } from '../interaction';
import { COURIER_INVOICES_BASE_PATH } from '../../../constants';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const matchesSearch = (row: CourierInvoiceListItem, term: string) => {
  if (!term.trim()) return true;
  const n = term.toLowerCase();
  const ref = row.supplier_reference?.toLowerCase() ?? '';
  const courier = row.courier?.name?.toLowerCase() ?? '';
  const idStr = String(row.id);
  return ref.includes(n) || courier.includes(n) || idStr.includes(n);
};

export const ListMain = () => {
  const { t } = useTranslation('courierInvoicesModule');
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { data, isError, error, refetch, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, isLoading } =
    useCourierInvoicesInfiniteList();

  const rows: CourierInvoiceListItem[] = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data?.pages]);

  const totalMeta = data?.pages[0]?.meta?.total ?? 0;
  const displayTotal = Math.max(totalMeta, rows.length);

  const filteredRows = useMemo(() => rows.filter((row) => matchesSearch(row, searchValue)), [rows, searchValue]);

  const hasSearchFilter = Boolean(searchValue.trim());
  const isBackgroundFetching = isFetching && !isFetchingNextPage;
  const isApiEmpty = !isLoading && rows.length === 0;
  const isFilteredEmpty = rows.length > 0 && filteredRows.length === 0 && hasSearchFilter;
  const showTable = filteredRows.length > 0;

  const breadcrumbs = useMemo(() => [{ label: t('breadcrumbs.courier_invoices') }], [t]);

  const headerActions = useMemo(
    () => [
      {
        id: 'courier-invoices-create',
        label: '',
        onClick: () => {},
        component: (
          <Button variant="primary" type="button" onClick={() => setCreateOpen(true)}>
            {t('list.create_button')}
          </Button>
        ),
      },
    ],
    [t],
  );

  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  if (isInitialLoad && isLoading) {
    return (
      <div className="px-6 pt-4">
        <LoadingState message={t('loading.list')} className="min-h-[280px]" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-6 pt-4">
        <ErrorState error={error ?? new Error(t('errors.unexpected'))} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="px-6 pt-4">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Search
            placeholder={t('list.search_placeholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onEnter={(value) => setSearchValue((value ?? inputValue).trim())}
            className="w-64"
            dataTestId="courier-invoices-search"
            inputDataTestId="courier-invoices-search-input"
          />
        </div>
        {displayTotal > 0 ? <TotalResults total={displayTotal} /> : null}
      </div>

      <div
        className={`transition-opacity duration-200 overflow-hidden ${
          isApiEmpty ? 'opacity-100 h-auto' : 'opacity-0 h-0 pointer-events-none'
        }`}
      >
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.DEFAULT}
          title={t('empty.title')}
          description={t('empty.description')}
        />
      </div>

      <div
        className={`mb-4 transition-opacity duration-200 overflow-hidden ${
          isFilteredEmpty ? 'opacity-100 h-auto' : 'opacity-0 h-0 pointer-events-none'
        }`}
      >
        <EmptyView
          type="noResults"
          icon={EMPTY_VIEW_ICONS.DEFAULT}
          title={t('empty.no_results_title')}
          description={t('empty.no_results_description')}
        />
      </div>

      <div
        className={`mb-4 transition-opacity duration-200 ${
          showTable ? 'opacity-100 h-auto' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
        }`}
      >
        <CourierInvoicesAgGridTable
          rows={filteredRows}
          basePath={COURIER_INVOICES_BASE_PATH}
          isFetching={isBackgroundFetching}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={() => void fetchNextPage()}
          hasNextPage={hasNextPage ?? false}
        />
      </div>

      <CreateCourierInvoiceModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
};
